// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { v4 as uuidv4 } from 'uuid';

export type Severity = 'error' | 'success' | 'info' | 'warning';

export type NotificationType = {
  id?: string;
  severity?: Severity;
  message?: string;
  autoClose?: boolean;
  autoCloseDelay?: number;
  replaceAll?: boolean;
};

export interface StateType {
  notifications: NotificationType[];
}

export enum ActionType {
  ADD = 0,
  REMOVE = 1,
  REMOVE_ALL = 2
}

export class Action<T> {
  type: ActionType;
  constructor(public payload?: T) {}
}
export class RemoveAction extends Action<string> {
  readonly type = ActionType.REMOVE;
}
export class RemoveAllAction extends Action<void> {
  readonly type = ActionType.REMOVE_ALL;
}
export class AddAction extends Action<NotificationType> {
  readonly type = ActionType.ADD;
}
export type Actions = RemoveAction | RemoveAllAction | AddAction;

export const initialState: StateType = {
  notifications: []
};

export const reducer = (state: StateType, action: Actions): StateType => {
  const { type } = action;

  switch (type) {
    case ActionType.ADD: {
      const notification = { id: uuidv4(), ...action.payload };
      const notifications = notification?.replaceAll
        ? [notification]
        : [...state.notifications, notification];
      return {
        ...state,
        notifications
      };
    }
    case ActionType.REMOVE: {
      const notifications = state.notifications.filter(
        (notification) => notification?.id !== action.payload
      );
      return {
        ...state,
        notifications
      };
    }
    case ActionType.REMOVE_ALL: {
      return {
        ...state,
        notifications: []
      };
    }
    default:
      throw new Error('Incorrect type in NotificationProvider');
  }
};
